import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, CalendarOff, X } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', monthly_target: '' });
  // ── Leaves dialog state ────────────────────────────────
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveEmployee, setLeaveEmployee] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [leaveLoading, setLeaveLoading] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error('فشل تحميل الموظفين');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmployee) {
        await axios.put(`${API_URL}/api/employees/${editingEmployee.id}`, {
          name: formData.name,
          phone: formData.phone,
          monthly_target: parseFloat(formData.monthly_target)
        });
        toast.success('تم تحديث الموظف بنجاح');
      } else {
        await axios.post(`${API_URL}/api/employees`, {
          name: formData.name,
          phone: formData.phone,
          monthly_target: parseFloat(formData.monthly_target)
        });
        toast.success('تم إضافة الموظف بنجاح');
      }
      
      setOpen(false);
      setFormData({ name: '', phone: '', monthly_target: '' });
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ الموظف');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      phone: employee.phone,
      monthly_target: employee.monthly_target.toString()
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموظف؟')) return;
    
    try {
      await axios.delete(`${API_URL}/api/employees/${id}`);
      toast.success('تم حذف الموظف بنجاح');
      fetchEmployees();
    } catch (error) {
      toast.error('فشل حذف الموظف');
    }
  };

  // ── Leaves ────────────────────────────────────────────
  const openLeaveDialog = async (employee) => {
    setLeaveEmployee(employee);
    setLeaveForm({ start_date: '', end_date: '', reason: '' });
    setLeaveOpen(true);
    await fetchLeaves(employee.id);
  };

  const fetchLeaves = async (employeeId) => {
    setLeaveLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/employees/${employeeId}/leaves`);
      setLeaves(r.data || []);
    } catch (e) {
      toast.error('فشل تحميل الإجازات');
    } finally {
      setLeaveLoading(false);
    }
  };

  const handleAddLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date) {
      toast.error('حدد تاريخ البداية والنهاية');
      return;
    }
    if (leaveForm.end_date < leaveForm.start_date) {
      toast.error('تاريخ النهاية قبل البداية');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/employees/${leaveEmployee.id}/leaves`, leaveForm);
      toast.success('تم تسجيل الإجازة');
      setLeaveForm({ start_date: '', end_date: '', reason: '' });
      await fetchLeaves(leaveEmployee.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تسجيل الإجازة');
    }
  };

  const handleDeleteLeave = async (id) => {
    if (!window.confirm('حذف هذه الإجازة؟')) return;
    try {
      await axios.delete(`${API_URL}/api/leaves/${id}`);
      toast.success('تم حذف الإجازة');
      await fetchLeaves(leaveEmployee.id);
    } catch (err) {
      toast.error('فشل حذف الإجازة');
    }
  };

  if (loading) {
    return <div className="text-center py-12">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-primary">إدارة الموظفين</h1>
          <p className="text-muted-foreground mt-1">أضف وعدّل الموظفين</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setEditingEmployee(null);
            setFormData({ name: '', phone: '', monthly_target: '' });
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-employee-button" className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة موظف
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir="rtl">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? 'تعديل موظف' : 'إضافة موظف جديد'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">الاسم</Label>
                <Input
                  id="name"
                  data-testid="employee-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-right"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">رقم الجوال</Label>
                <Input
                  id="phone"
                  data-testid="employee-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="text-right"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target">الهدف الشهري <SAR /></Label>
                <Input
                  id="target"
                  data-testid="employee-target-input"
                  type="number"
                  step="0.01"
                  value={formData.monthly_target}
                  onChange={(e) => setFormData({ ...formData, monthly_target: e.target.value })}
                  className="text-right"
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  إلغاء
                </Button>
                <Button type="submit" data-testid="save-employee-button">
                  {editingEmployee ? 'تحديث' : 'حفظ'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الاسم</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">رقم الجوال</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الهدف الشهري</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-muted-foreground">
                    لا توجد موظفين
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <tr key={employee.id} className="border-t border-border hover:bg-accent/50 transition-colors">
                    <td className="py-4 px-6 text-foreground">{employee.name}</td>
                    <td className="py-4 px-6 text-foreground font-numbers">{employee.phone}</td>
                    <td className="py-4 px-6 text-foreground font-numbers">
                      {employee.monthly_target.toLocaleString('en-US')} <SAR />
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openLeaveDialog(employee)}
                          data-testid={`leave-employee-${employee.id}`}
                          className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                          title="تحديد إجازة"
                        >
                          <CalendarOff className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(employee)}
                          data-testid={`edit-employee-${employee.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(employee.id)}
                          data-testid={`delete-employee-${employee.id}`}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Leaves dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="bg-white max-w-lg" dir="rtl" data-testid="leave-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">
              إجازات الموظف{leaveEmployee ? ` — ${leaveEmployee.name}` : ''}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddLeave} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div>
              <Label htmlFor="leave-start">من تاريخ</Label>
              <Input
                id="leave-start"
                type="date"
                value={leaveForm.start_date}
                onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                data-testid="leave-start-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="leave-end">إلى تاريخ</Label>
              <Input
                id="leave-end"
                type="date"
                value={leaveForm.end_date}
                onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                data-testid="leave-end-input"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="leave-reason">السبب (اختياري)</Label>
              <Input
                id="leave-reason"
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                data-testid="leave-reason-input"
                placeholder="مثلاً: إجازة اعتيادية"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" data-testid="save-leave-button">
                <Plus className="h-4 w-4 ml-1" /> إضافة إجازة
              </Button>
            </div>
          </form>

          <div className="mt-4 border-t border-border pt-3">
            <div className="text-sm font-bold mb-2">الإجازات المسجلة</div>
            {leaveLoading ? (
              <div className="text-sm text-muted-foreground">جاري التحميل...</div>
            ) : leaves.length === 0 ? (
              <div className="text-sm text-muted-foreground">لا توجد إجازات مسجلة</div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {leaves.map((lv) => (
                  <div
                    key={lv.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-orange-50 border border-orange-200"
                    data-testid={`leave-row-${lv.id}`}
                  >
                    <div className="text-sm">
                      <div className="font-bold font-numbers" dir="ltr">
                        {lv.start_date} → {lv.end_date}
                      </div>
                      {lv.reason && <div className="text-xs text-muted-foreground">{lv.reason}</div>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLeave(lv.id)}
                      className="h-7 w-7 p-0 text-destructive"
                      data-testid={`delete-leave-${lv.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}